import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
  writeBatch,
  type FieldValue,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './auth-context';
import { PREVIEW_ROLE_LABEL } from './previewRole';
import { schoolYearKeyForPeriod } from './shoutoutWall';
import { awardWinDocId } from './studentAwardWins';
import { useUserDoc, type UserDoc, type UserRole } from './users';
import { getAuth } from 'firebase/auth';
import { functionHeaders } from './functionSecurity';
import { isOwnJrStaffSignIn } from './jrStaffAliases.ts';
import type { MessageAttachment } from './threads';
import { saveMessageWithOptionalAttachments } from './messageAttachmentWrite';
import {
  AWARD_ROUND_ROSTER_COLLECTION,
  AWARD_TYPE_LABELS,
  AWARD_WHY_MIN_CHARS,
  awardRoleCanNominate,
  awardRoundRosterDocId,
  awardWhyIsEnough,
  siblingPickBlocksSave,
  type AwardRoundRosterSnapshot,
} from './awardNominators.ts';
// The calendar (ported from Codex #1247) and the ONE mapping from a stored
// period key to the month a person reads, shared with the morning reminder.
import {
  awardOpenRound,
  awardRoundClosedMessage,
  awardRoundTakesNominations,
  awardTaskDueDate,
  recognizedAwardMonthKey,
  recognizedAwardMonthLabel,
} from './studentAwardSchedule.ts';
import type { StrayMergeChoices } from './awardStrayMerge.ts';
export { awardTaskDueDate, recognizedAwardMonthKey, recognizedAwardMonthLabel };

// One definition of when a school year starts, in lib/shoutoutWall.ts (which
// imports nothing, so the Shout-out Wall's month math can be driven directly
// by node --experimental-strip-types). Re-exported here because every existing
// caller imports it from this module and because two copies of that fact would
// drift - the wall's month chips and its trophy tally both depend on them
// agreeing.
export { schoolYearKeyForPeriod };

export type StudentAwardType = 'student-of-month' | 'jr-spotlight';
/**
 * 'no-pick' is the nominator's own "No pick this month" (DECISIONS 170),
 * written only by netlify/functions/studentAwardAction.mjs noPick: it names
 * no dancer, counts as answered for the to-do, the morning reminder and the
 * send-back lane, and is replaced by a real pick while the round is open.
 * lib/awardNominators.ts isLiveAwardPick says which statuses are a pick.
 */
export type StudentAwardStatus = 'pending' | 'approved' | 'rejected' | 'no-pick';

// One definition, in lib/awardNominators.ts, shared with the morning
// reminder so the page and the to-do name an award the same way.
export const STUDENT_AWARD_LABELS: Record<StudentAwardType, string> = AWARD_TYPE_LABELS;

export interface StudentAwardNominationDoc {
  id: string;
  periodKey: string;
  schoolYearKey: string;
  awardType: StudentAwardType;
  nominatorUid: string;
  nominatorName: string;
  nominatorRole: UserRole;
  studentId: string;
  studentName: string;
  note: string;
  attachments?: MessageAttachment[];
  status: StudentAwardStatus;
  reviewedBy: string | null;
  reviewedAt: Timestamp | FieldValue | null;
  createdAt: Timestamp | FieldValue;
  createdBy: string;
  updatedAt: Timestamp | FieldValue;
  /**
   * Why Kim sent this pick back (Kim, 2026-09-28: "there isn't a way for me
   * to 'reject' someone with an explanation"). Server-written by
   * netlify/functions/studentAwardAction.mjs on a send-back, removed by a
   * fresh pick or a Reopen. Read it only while status is 'rejected'.
   */
  returnReason?: string;
  returnedByName?: string;
  returnedAt?: Timestamp | FieldValue | null;
}

/** Why Kim sent this pick back, or null when it is not sent back. */
export function nominationReturnReason(nomination: Pick<StudentAwardNominationDoc, 'status' | 'returnReason'> | null | undefined): string | null {
  if (!nomination || nomination.status !== 'rejected') return null;
  const reason = typeof nomination.returnReason === 'string' ? nomination.returnReason.trim() : '';
  return reason || null;
}

export interface UseStudentAwardNominationsResult {
  loading: boolean;
  nominations: StudentAwardNominationDoc[];
  error: string | null;
}

/** The Director's read of every pick, which can also say when it is only the saved copy. */
export interface UseAllStudentAwardNominationsResult extends UseStudentAwardNominationsResult {
  /**
   * True while the newest snapshot came from this device's saved copy (the
   * persistent cache) and the server has not answered yet. Only reported when
   * the caller asks for it (`includeMetadataChanges`); false otherwise.
   * `loading` goes false on the FIRST snapshot, which may be that saved copy,
   * so anything that must not act on a copy older than a pick approved on
   * another device (printing certificates) reads this too.
   */
  fromCache: boolean;
}

export function monthKey(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/** 'YYYY-MM' moved forward or back by whole months. */
export function shiftMonthKey(periodKey: string, delta: number): string {
  const [year, month] = periodKey.split('-').map(Number);
  if (!year || !month) return periodKey;
  return monthKey(new Date(year, month - 1 + delta, 1));
}

/**
 * The round a nominator sees: the ONE open round, or while none is open, the
 * one coming up (lib/studentAwardSchedule.ts awardOpenRound). Kim,
 * 2026-09-28: "So right now, the only one that should be showing is
 * September 2026". A round is STORED under the month after the one it
 * recognizes, so on September 29, 2026 this is 2026-10, which every label
 * reads as September 2026 (recognizedAwardMonthLabel). It used to follow the
 * 15th of the month and let the month arrows walk anywhere, which is how picks
 * for one round ended up spread across three stored months.
 */
export function defaultAwardPeriodKey(now = new Date()): string {
  return awardOpenRound(now).period;
}

/**
 * The month a nomination for `periodKey` is actually made in, which is also
 * the month it recognizes: the one before it. Nominations open fifteen days
 * before that month's last day and the to-do goes out ten days before it
 * (Bobbie, 2026-10-26; five days for both until then), due on the last day,
 * which is what the
 * scheduled studentAwardsReminder function enforces
 * (lib/studentAwardSchedule.ts holds the calendar for both).
 */
export function nominationMonthKey(periodKey: string): string {
  return shiftMonthKey(periodKey, -1);
}

export function monthLabel(periodKey: string): string {
  const [year, month] = periodKey.split('-').map(Number);
  if (!year || !month) return periodKey;
  return new Date(year, month - 1, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
}

export function nominationDocId(
  periodKey: string,
  awardType: StudentAwardType,
  nominatorUid: string
): string {
  return `${periodKey}_${awardType}_${nominatorUid}`;
}

// The deterministic to-do id (`studentAwards_{period}_{awardType}_{uid}`),
// the period and award read back off it, and the "never marked done by hand"
// words live in lib/studentAwardTask.ts, which is Firebase-free so the task
// rows' node tests import it. Re-exported here because every existing caller
// (lib/taskSources.ts, lib/tileCounts.ts) imports them from this module.
export {
  AWARD_TASK_ID_PREFIX,
  awardPeriodFromTaskId,
  awardTaskId,
} from './studentAwardTask.ts';

/**
 * Mirrors canNominateAwardType() in shared/firestore.rules. Manager joined
 * Student of the Month 2026-08-13 on the union ruling (an Executive Teacher
 * nominates, so a manager does too). Monthly Spotlight stays Jr Staff's to
 * give: neither foh nor company holds it, so it sits outside the union. The
 * list itself lives in lib/awardNominators.ts, which the reminder reads too.
 */
export function canNominateAward(
  role: UserRole | null | undefined,
  awardType: StudentAwardType
): boolean {
  return awardRoleCanNominate(role, awardType);
}

export function awardTypesForRole(role: UserRole | null | undefined): StudentAwardType[] {
  if (role === 'director') return ['student-of-month', 'jr-spotlight'];
  if (role === 'company' || role === 'manager') return ['student-of-month'];
  if (role === 'ensemble') return ['jr-spotlight'];
  return [];
}

// WHO OWES A PICK is awardNominators() in lib/awardNominators.ts, called by
// the review board, the Post congratulations card and the morning reminder
// alike. The role-only owesStudentOfMonth() that used to live here is gone
// with the ruling that replaced it (Bobbie, 2026-09-28: teachers, managers
// and directors "who teach a class", and every Jr Staff once per person).

// awardTaskDueDate (re-exported above) is when a round's to-do is due: the
// same instant the reminder stamps on the to-do it writes, from the same
// function in lib/studentAwardSchedule.ts, transition rounds included.

/**
 * The roster the morning reminder keeps for one round
 * (studioSettings/awardRoundRoster_<period>, lib/awardNominators.ts
 * awardRoundNominators), for Kim's board, so the page names exactly the
 * people the reminder chases. `snapshot` is null while no roster is stored
 * yet (a round the reminder has not reached); `loading` is true until the
 * first answer. Every active staff account may read studioSettings.
 */
export function useAwardRoundRoster(
  periodKey: string,
  enabled: boolean
): { snapshot: AwardRoundRosterSnapshot | null; loading: boolean } {
  const [state, setState] = useState<{ key: string; snapshot: AwardRoundRosterSnapshot | null } | null>(null);
  useEffect(() => {
    if (!enabled || !db) return;
    const ref = doc(db, AWARD_ROUND_ROSTER_COLLECTION, awardRoundRosterDocId(periodKey));
    return onSnapshot(
      ref,
      (snap) => setState({ key: periodKey, snapshot: snap.exists() ? (snap.data() as AwardRoundRosterSnapshot) : null }),
      // Unreadable reads as "not stored yet": the board then shows today's
      // answer, which is what it showed before the roster was kept.
      () => setState({ key: periodKey, snapshot: null })
    );
  }, [periodKey, enabled]);
  if (!enabled) return { snapshot: null, loading: false };
  if (!state || state.key !== periodKey) return { snapshot: null, loading: true };
  return { snapshot: state.snapshot, loading: false };
}

/**
 * Several rounds' stored rosters at once, read ONCE (getDoc, no listener), for
 * the Director's Reports tab: who each round asked, so "Teachers with no pick"
 * and "never sent" count real people. The page calls it only once that tab is
 * open, never on page load. A round with no stored roster (every round before
 * the reminder began keeping one) comes back null, and the report says so
 * rather than guessing.
 */
export function useAwardRoundRosters(
  periodKeys: readonly string[],
  enabled: boolean
): { snapshots: ReadonlyMap<string, AwardRoundRosterSnapshot | null>; loading: boolean } {
  const key = periodKeys.join(',');
  const [state, setState] = useState<{ key: string; snapshots: Map<string, AwardRoundRosterSnapshot | null> } | null>(null);
  useEffect(() => {
    if (!enabled || !db) return;
    const firestore = db;
    let live = true;
    const periods = key ? key.split(',') : [];
    Promise.all(periods.map(async (period) => {
      try {
        const snap = await getDoc(doc(firestore, AWARD_ROUND_ROSTER_COLLECTION, awardRoundRosterDocId(period)));
        return [period, snap.exists() ? (snap.data() as AwardRoundRosterSnapshot) : null] as const;
      } catch {
        return [period, null] as const;
      }
    })).then((rows) => {
      if (live) setState({ key, snapshots: new Map(rows) });
    });
    return () => { live = false; };
  }, [key, enabled]);
  if (!enabled) return { snapshots: new Map(), loading: false };
  if (!state || state.key !== key) return { snapshots: new Map(), loading: true };
  return { snapshots: state.snapshots, loading: false };
}

/** A nomination one of the viewer's OTHER sign-ins holds for a round. */
export interface SiblingAwardPick {
  awardType: StudentAwardType;
  nominatorUid: string;
  status: StudentAwardStatus;
  studentId: string;
  studentName: string;
  /** Only on a pick Kim sent back: the reason she gave, and who sent it. */
  returnReason?: string;
  returnedByName?: string;
}

/**
 * Every nomination the signed-in Jr Staff's OTHER sign-ins hold for one
 * round (netlify/functions/awardNominationSiblings.mjs). A Jr Staff is ONE
 * person however she signs in (Codex review of #1252, P1): the rules let a
 * sign-in read only its own pending pick, so the server answers for her.
 * Throws on any failure; callers decide what a failure means.
 */
export async function fetchSiblingAwardPicks(periodKey: string): Promise<SiblingAwardPick[]> {
  const me = getAuth().currentUser;
  if (!me) throw new Error('Sign in required.');
  const response = await fetch('/.netlify/functions/awardNominationSiblings', {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: await functionHeaders(me),
    body: JSON.stringify({ periodKey }),
  });
  const payload = (await response.json().catch(() => ({}))) as { picks?: unknown; error?: string };
  if (!response.ok || !Array.isArray(payload.picks)) {
    throw new Error(payload.error ?? 'Could not check your other sign-in.');
  }
  return payload.picks.flatMap((row): SiblingAwardPick[] => {
    const one = row as Partial<SiblingAwardPick>;
    if (one.awardType !== 'student-of-month' && one.awardType !== 'jr-spotlight') return [];
    const status: StudentAwardStatus = one.status === 'approved' || one.status === 'rejected' || one.status === 'no-pick' ? one.status : 'pending';
    const returnReason = status === 'rejected' && typeof one.returnReason === 'string' ? one.returnReason.trim() : '';
    return [{
      awardType: one.awardType,
      nominatorUid: String(one.nominatorUid ?? ''),
      status,
      studentId: String(one.studentId ?? ''),
      studentName: String(one.studentName ?? ''),
      ...(returnReason
        ? { returnReason, returnedByName: typeof one.returnedByName === 'string' ? one.returnedByName.trim() : '' }
        : {}),
    }];
  });
}

/**
 * The viewer's other sign-ins' picks for a round, for the nomination card.
 * Only a Jr Staff sign-in (isOwnJrStaffSignIn, lib/jrStaffAliases.ts) asks;
 * everyone else holds one sign-in and gets an empty list with no request.
 * Asked again whenever the tab comes back into view, so a pick sent on her
 * phone folds the card on her laptop.
 */
export function useSiblingAwardPicks(
  periodKey: string,
  viewer: Pick<UserDoc, 'uid' | 'role' | 'status' | 'studentId'> | null | undefined
): SiblingAwardPick[] {
  const enabled = !!viewer && isOwnJrStaffSignIn(viewer);
  const [state, setState] = useState<{ key: string; picks: SiblingAwardPick[] }>({ key: '', picks: [] });
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const load = () => {
      fetchSiblingAwardPicks(periodKey)
        .then((picks) => { if (!cancelled) setState({ key: periodKey, picks }); })
        // A failed check leaves the card as it would be without one; the
        // save itself checks again before it writes.
        .catch(() => { if (!cancelled) setState({ key: periodKey, picks: [] }); });
    };
    load();
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [periodKey, enabled]);
  return enabled && state.key === periodKey ? state.picks : [];
}

export const SIBLING_PICK_MESSAGE =
  'You already sent this month\'s pick from your other sign-in. To change it, open Student Awards there.';

export function useMyStudentAwardNominations(
  periodKey = defaultAwardPeriodKey()
): UseStudentAwardNominationsResult {
  const { user, loading: authLoading } = useAuth();
  const { userDoc, loading: meLoading } = useUserDoc();
  const [nominations, setNominations] = useState<StudentAwardNominationDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const awardTypes = useMemo(() => awardTypesForRole(userDoc?.role), [userDoc?.role]);

  useEffect(() => {
    if (authLoading || meLoading) return;
    if (!db || !user || !userDoc || userDoc.status !== 'active' || awardTypes.length === 0) {
      setNominations([]);
      setError(null);
      setLoading(false);
      return;
    }
    const next: Record<string, StudentAwardNominationDoc> = {};
    setLoading(true);
    setError(null);
    const unsubs = awardTypes.map((awardType) => {
      const ref = doc(db!, 'studentAwardNominations', nominationDocId(periodKey, awardType, user.uid));
      return onSnapshot(
        ref,
        (snap) => {
          if (snap.exists()) next[awardType] = { id: snap.id, ...(snap.data() as Omit<StudentAwardNominationDoc, 'id'>) };
          else delete next[awardType];
          setNominations(Object.values(next));
          setLoading(false);
        },
        (err) => {
          setError(err.message);
          setLoading(false);
        }
      );
    });
    return () => unsubs.forEach((unsub) => unsub());
  }, [authLoading, meLoading, user, userDoc, periodKey, awardTypes]);

  return { loading, nominations, error };
}

/**
 * A nominator's own picks from prior months, newest month first. Excludes the
 * current period since that one already lives in the main nomination form.
 * Requires a composite index on (nominatorUid ASC, periodKey DESC) so the
 * `nominatorUid == request.auth.uid` rules check stays provable for a list
 * query (see shared/firestore.indexes.json).
 */
export function useMyPastStudentAwardNominations(
  currentPeriodKey = defaultAwardPeriodKey()
): UseStudentAwardNominationsResult {
  const { user, loading: authLoading } = useAuth();
  const { userDoc, loading: meLoading } = useUserDoc();
  const [nominations, setNominations] = useState<StudentAwardNominationDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading || meLoading) return;
    if (!db || !user || !userDoc || userDoc.status !== 'active') {
      setNominations([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const q = query(
      collection(db, 'studentAwardNominations'),
      where('nominatorUid', '==', user.uid),
      orderBy('periodKey', 'desc')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as Omit<StudentAwardNominationDoc, 'id'>) }))
          .filter((n) => n.periodKey !== currentPeriodKey);
        setNominations(rows);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [authLoading, meLoading, user, userDoc, currentPeriodKey]);

  return { loading, nominations, error };
}

// There is deliberately NO "every approved nomination" hook for families here.
// An earlier cut of the Shout-out Wall had one, backed by a rule that opened
// approved nominations to any active user. Firestore cannot hide fields on a
// read, so that hook handed families the note a teacher wrote for Kim along
// with the winner's name. The wall reads lib/studentAwardWins.ts instead.

export function useStudentAwardNominations(
  enabled = true,
  options: { includeMetadataChanges?: boolean } = {}
): UseAllStudentAwardNominationsResult {
  const includeMetadataChanges = options.includeMetadataChanges === true;
  const { userDoc, loading: meLoading } = useUserDoc();
  const [nominations, setNominations] = useState<StudentAwardNominationDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(false);

  useEffect(() => {
    if (meLoading) return;
    if (!enabled || !db || !userDoc || userDoc.status !== 'active' || userDoc.role !== 'director') {
      setNominations([]);
      setError(null);
      setFromCache(false);
      setLoading(false);
      return;
    }
    const q = query(collection(db, 'studentAwardNominations'), orderBy('createdAt', 'desc'));
    let first = true;
    const unsub = onSnapshot(
      q,
      { includeMetadataChanges },
      (snap) => {
        // Asked for metadata changes, a snapshot with the same documents (the
        // saved copy becoming the server's answer) must not hand the caller a
        // new array. Everyone else gets every snapshot, as before.
        if (!includeMetadataChanges || first || snap.docChanges().length > 0) {
          setNominations(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<StudentAwardNominationDoc, 'id'>) })));
        }
        first = false;
        setFromCache(includeMetadataChanges && snap.metadata.fromCache);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [enabled, meLoading, userDoc, includeMetadataChanges]);

  return { loading, nominations, error, fromCache };
}

export interface SaveStudentAwardNominationInput {
  periodKey: string;
  awardType: StudentAwardType;
  studentId: string;
  studentName: string;
  note: string;
  attachments?: MessageAttachment[];
}

const WHY_REQUIRED_MESSAGE = `Write a sentence about why this dancer, at least ${AWARD_WHY_MIN_CHARS} characters. A Director needs the reason, not just the name.`;

export const STUDENT_AWARD_ACTION_ENDPOINT = '/.netlify/functions/studentAwardAction';

const AWARD_ACTION_MESSAGES: Record<string, string> = {
  why_required: WHY_REQUIRED_MESSAGE,
  reason_required: 'Tell them why before you send it back.',
  // No pick this month over a pick already in (studentAwardAction.mjs noPick).
  // Not `conflict`, whose words are about her other sign-in.
  pick_in: 'Your pick for this month is already in. Change the dancer instead, or ask a Director to send it back.',
  already_approved: 'A Director has already approved this one. Ask a Director to reopen it if it needs to change.',
  not_allowed: 'This award is not assigned to your staff group.',
  student_not_found: 'We could not find that dancer any more. Search again.',
  not_found: 'That nomination is no longer there. Refresh the page.',
  too_many_requests: 'That is a lot of saving. Give it a minute and try again.',
  sign_in_required: 'Sign in again, then try once more.',
  server_not_configured: 'The studio server is not set up for this yet.',
  // The Jr Staff MVP (netlify/functions/studentAwardAction.mjs, mvp* actions).
  already_decided: "This month's Jr Staff MVP is already decided.",
  not_asked: 'The Jr Staff MVP vote is for teachers, managers and directors who teach a current class.',
  not_a_nominee: 'Only a Jr Staff who got a vote this month can be the MVP.',
  // The MVP vote rides the Student of the Month to-do and closes with it
  // (lib/studentAwardSchedule.ts AWARD_NOMINATIONS_CLOSE_DAY).
  award_round_closed: 'Votes for that month are closed, so this vote was not sent.',
  conflict: SIBLING_PICK_MESSAGE,
  // The wall step while the Shout-out Wall is paused (lib/wallPause.ts, #1272).
  announcements_paused: 'Paused until the awards update is finished: nothing went on the wall and nobody was told.',
  bad_request: 'That did not go through. Refresh the page and try again.',
};

/**
 * The server lane for award writes a button alone must not be trusted with
 * (netlify/functions/studentAwardAction.mjs): a send-back with its reason, a
 * reopen, the Jr Staff MVP, and the nominator's own No pick this month
 * (useSendNoPick below, DECISIONS 170). A PICK never comes here: every pick,
 * a photo or not, goes through the nominate lane (useSaveStudentAwardNomination
 * below), the one door that checks the open round. Errors come back as plain
 * words.
 */
export async function callStudentAwardAction(
  user: NonNullable<ReturnType<typeof useAuth>['user']>,
  payload: Record<string, unknown>,
  options: { timeoutMs?: number } = {}
): Promise<Record<string, unknown>> {
  // Opt-in: a READ that must not hold a page forever (the Jr Staff MVP votes)
  // passes a timeout; a stalled call then ends as the same plain failure a bad
  // answer does. AbortSignal.timeout() is not there on the older iPhones this
  // app supports, so it is an AbortController and a timer.
  const controller = options.timeoutMs ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), options.timeoutMs) : undefined;
  try {
    const response = await fetch(STUDENT_AWARD_ACTION_ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: await functionHeaders(user),
      body: JSON.stringify(payload),
      ...(controller ? { signal: controller.signal } : {}),
    });
    const body = (await response.json().catch(() => ({}))) as { error?: string } & Record<string, unknown>;
    if (!response.ok || body.error) {
      throw new Error(AWARD_ACTION_MESSAGES[body.error ?? ''] ?? "Couldn't save that right now. Try again in a moment.");
    }
    return body;
  } catch (err) {
    if (controller?.signal.aborted) throw new Error("Couldn't save that right now. Try again in a moment.");
    throw err;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

export interface PublishMonthWallResult {
  /** Distinct approved dancer and award cards for the month. */
  cards: number;
  /** Card ids this tap put up (a second tap puts up none). */
  created: string[];
  /** Every card id for the month that is on the wall now, the MVP's included. */
  onWall: string[];
  /** The month's Jr Staff MVP, put up with them; told: sign-ins told by this tap. */
  mvp: { name: string; told: number } | null;
}

/**
 * THE WALL STEP (Bobbie, 2026-09-29: "for the award section, I think it should
 * refuse anything that doesn't come from the green put on the wall button").
 * The server writes every card (netlify/functions/studentAwardAction.mjs
 * publishMonthWall); the rules refuse a card from any browser. Director only.
 */
export async function requestPublishMonthWall(
  user: NonNullable<ReturnType<typeof useAuth>['user']>,
  periodKey: string
): Promise<PublishMonthWallResult> {
  const body = await callStudentAwardAction(user, { action: 'publishMonthWall', periodKey });
  return {
    cards: typeof body.cards === 'number' ? body.cards : 0,
    created: Array.isArray(body.created) ? body.created.filter((id): id is string => typeof id === 'string') : [],
    onWall: Array.isArray(body.onWall) ? body.onWall.filter((id): id is string => typeof id === 'string') : [],
    mvp: body.mvp && typeof body.mvp === 'object'
      ? {
        name: String((body.mvp as { name?: unknown }).name ?? ''),
        told: Number((body.mvp as { told?: unknown }).told ?? 0) || 0,
      }
      : null,
  };
}

/** Kim sends a pick back, with the reason the nominator will read. */
export function useSendAwardPickBack() {
  const { user } = useAuth();
  return async (nominationId: string, reason: string): Promise<void> => {
    if (!user) throw new Error('Sign in required.');
    if (!reason.trim()) throw new Error(AWARD_ACTION_MESSAGES.reason_required);
    await callStudentAwardAction(user, { action: 'sendBack', nominationId, reason: reason.trim() });
  };
}

/** Kim reopens a pick: back to waiting on her, any send-back reason cleared. */
export function useReopenAwardPick() {
  const { user } = useAuth();
  return async (nominationId: string): Promise<void> => {
    if (!user) throw new Error('Sign in required.');
    await callStudentAwardAction(user, { action: 'reopen', nominationId });
  };
}

/** What the server answers to No pick this month. */
export interface SendNoPickResult {
  /** True when her to-do is ticked now; false while her Jr Staff MVP vote is still owed. */
  todoDone: boolean;
}

/**
 * NO PICK THIS MONTH (Bobbie, 2026-11-01, DECISIONS 170: "Yes", anyone may
 * need to close the Nominate to-do without sending a pick). The server
 * records it on her own nomination doc (status 'no-pick'), ticks her to-do on
 * every sign-in she holds the way a real pick does (the Jr Staff MVP vote
 * still owed where the round asks for it), and a real pick sent later, while
 * the round is open, replaces it. The same checks the nominate lane runs
 * first, in plain words, and the server runs them again.
 */
export function useSendNoPick() {
  const { user } = useAuth();
  const { userDoc, previewRole } = useUserDoc();
  return async (input: { periodKey: string; awardType: StudentAwardType }): Promise<SendNoPickResult> => {
    if (!user || !userDoc || userDoc.status !== 'active') throw new Error('Sign in required.');
    if (previewRole) {
      throw new Error(
        `You are previewing as ${PREVIEW_ROLE_LABEL[previewRole]}, so actions are turned off. Leave preview to send a real nomination.`
      );
    }
    if (!canNominateAward(userDoc.role, input.awardType)) {
      throw new Error('This award is not assigned to your staff group.');
    }
    if (!awardRoundTakesNominations(input.periodKey, new Date())) {
      throw new Error(awardRoundClosedMessage(input.periodKey, new Date()));
    }
    const body = await callStudentAwardAction(user, { action: 'noPick', periodKey: input.periodKey, awardType: input.awardType });
    return { todoDone: body.todoDone === true };
  };
}

export function useSaveStudentAwardNomination() {
  const { user } = useAuth();
  const { userDoc, previewRole } = useUserDoc();
  return async (input: SaveStudentAwardNominationInput): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user || !userDoc || userDoc.status !== 'active') throw new Error('Sign in required.');
    // Role preview is a display override only: the request would still run as
    // the Director's real account, and the rules re-check the caller's real
    // role against the nominatorRole in the payload, so the write is denied
    // with a raw permissions error (Bobbie hit exactly this previewing as Jr
    // Assistant). Refuse at the action, out loud, in plain words instead.
    if (previewRole) {
      throw new Error(
        `You are previewing as ${PREVIEW_ROLE_LABEL[previewRole]}, so actions are turned off. Leave preview to send a real nomination.`
      );
    }
    if (!canNominateAward(userDoc.role, input.awardType)) {
      throw new Error('This award is not assigned to your staff group.');
    }
    // ONE ROUND AT A TIME, asked at the moment of sending, in studio time, so
    // a card left open past the close day cannot slip a late pick in. The
    // server lane refuses the same thing (netlify/functions/lib/
    // messageAttachmentWrite.mjs, award_round_closed).
    if (!awardRoundTakesNominations(input.periodKey, new Date())) {
      throw new Error(awardRoundClosedMessage(input.periodKey, new Date()));
    }
    if (!input.studentId) throw new Error('Pick a dancer first.');
    const trimmedName = input.studentName.trim();
    if (!trimmedName) throw new Error('The selected dancer needs a name.');
    // The button already waits for this; the server refuses it too.
    if (!awardWhyIsEnough(userDoc.role, input.note)) throw new Error(WHY_REQUIRED_MESSAGE);

    const nominationRef = doc(
      db,
      'studentAwardNominations',
      nominationDocId(input.periodKey, input.awardType, user.uid)
    );
    // ONE PERSON, ONE PICK A ROUND (Codex review of #1252, P1). Asked fresh
    // at the moment of sending, not from what the card last saw, and only
    // when this sign-in holds no pick of its own for the round (changing her
    // own pick is always hers to do). A failed check does not block the send:
    // the reminder and Kim's board count her once either way, and the
    // server lane refuses a second pick on its own.
    const own = await getDoc(nominationRef).catch(() => null);
    if (own?.exists() && (own.data() as StudentAwardNominationDoc).status === 'approved') {
      throw new Error(ALREADY_APPROVED_MESSAGE);
    }
    const jrStaff = isOwnJrStaffSignIn(userDoc);
    if (jrStaff && !own?.exists()) {
      const siblings = await fetchSiblingAwardPicks(input.periodKey).catch(() => []);
      if (siblingPickBlocksSave(siblings, input.awardType)) throw new Error(SIBLING_PICK_MESSAGE);
    }
    // EVERY PICK GOES THROUGH THE SERVER LANE, a photo or not (refute of
    // #1266, P1). The page used to write a pick with no photo straight into
    // Firestore, and the rules accept a pick for any month, so the browser's
    // own open-round check above was the only thing in front of a closed or
    // not-yet-open month: a tab still holding the old page (month arrows and
    // all) could recreate the stray months. The lane
    // (netlify/functions/lib/messageAttachmentWrite.mjs, awardNomination)
    // refuses any round that is not the open one, checks the role, the
    // dancer and the one-pick rule, and ticks her to-do in the same
    // transaction. The page never writes studentAwardNominations itself.
    // STILL OPEN, recorded for Bobbie in TASK-QUEUE.md: the rules' create
    // and update clauses carry no period check, so a hand-built client can
    // still write any month straight to Firestore until a rules change adds
    // one.
    try {
      await saveMessageWithOptionalAttachments({
        user,
        action: 'awardNomination',
        targetId: nominationRef.id,
        writeId: crypto.randomUUID(),
        payload: {
          periodKey: input.periodKey,
          awardType: input.awardType,
          studentId: input.studentId,
          studentName: trimmedName,
          note: input.note.trim().slice(0, 1000),
        },
        attachments: input.attachments ?? [],
      });
    } catch (error) {
      throw new Error(nominationLaneErrorMessage(error, input.periodKey, jrStaff));
    }
  };
}

const ALREADY_APPROVED_MESSAGE = 'A Director has already approved this one. Ask a Director to reopen it if it needs to change.';

/**
 * Plain words for a pick the nominate lane did not take. The lane's own codes
 * ride on the error (lib/messageAttachmentWrite.ts sets `.code`); the ones a
 * nominator can act on get their own sentence, the lane's other refusals keep
 * the plain sentence it already carries, and anything with no code (the
 * network dropped, the page could not reach the studio) gets the stable
 * "try again" wording instead of a browser's raw "Failed to fetch".
 */
export function nominationLaneErrorMessage(error: unknown, periodKey: string, jrStaff: boolean): string {
  const code = error && typeof error === 'object' ? String((error as { code?: unknown }).code ?? '') : '';
  if (code === 'award_round_closed') return awardRoundClosedMessage(periodKey, new Date());
  // Kim, 2026-09-28: senior staff write a sentence of why. The lane refuses a
  // name alone for them (awardWhyIsEnough), whatever this page checked first.
  if (code === 'why_required') return WHY_REQUIRED_MESSAGE;
  if (code === 'conflict' && jrStaff) return SIBLING_PICK_MESSAGE;
  if (code === 'not_allowed') {
    return "That pick didn't go through. If a Director has already approved it, ask a Director to reopen it; otherwise try Send nomination again, and if it keeps happening, tell Bobbie.";
  }
  if (code && error instanceof Error && error.message) return error.message;
  // eslint-disable-next-line no-console
  console.error('[studentAwards] save nomination failed', error);
  return "Couldn't save that pick right now. Try again in a moment.";
}

/** True when two nominations would produce the same public wall card. */
function sameWinCard(a: StudentAwardNominationDoc, b: StudentAwardNominationDoc): boolean {
  return a.periodKey === b.periodKey && a.awardType === b.awardType && a.studentId === b.studentId;
}

/**
 * Kim's approve / reject / reopen.
 *
 * APPROVING IS PRIVATE NOW. Kim, 2026-09-28: "right now we haven't released
 * this to the parents yet. So if it's, it looks like it's going out, family
 * see the answer, the recognition off, I don't want that yet." Approving used
 * to write the family-visible Shout-out Wall card in the same batch, so every
 * approval went straight to every family's home screen. Now it only marks the
 * winner on Kim's list; putting a month's winners on the wall is its own step
 * with its own confirm (requestPublishMonthWall below, the server's
 * publishMonthWall; since 2026-09-29 no browser can write a wall card).
 *
 * Rejecting or reopening still takes a card down, in one batch with the
 * status, so a card already on the wall never outlives the approval behind it.
 * `peers` is every nomination the Director can already see, used to answer
 * one question without another read: is some OTHER approved pick still
 * backing this same dancer, award and month? Two teachers can nominate the
 * same dancer, and reopening one of them must not wipe a card the other still
 * earns.
 */
export function useSetStudentAwardStatus() {
  const { user } = useAuth();
  return async (
    nomination: StudentAwardNominationDoc,
    status: StudentAwardStatus,
    peers: readonly StudentAwardNominationDoc[] = []
  ): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const batch = writeBatch(db);
    batch.update(doc(db, 'studentAwardNominations', nomination.id), {
      status,
      reviewedBy: status === 'pending' ? null : user.uid,
      reviewedAt: status === 'pending' ? null : serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    if (status !== 'approved') {
      const stillEarned = peers.some(
        (peer) => peer.id !== nomination.id && peer.status === 'approved' && sameWinCard(peer, nomination)
      );
      // delete() on a card that was never published is a no-op, and the rule
      // does not read `resource`, so reopening a never-published pick is safe.
      if (!stillEarned) {
        batch.delete(doc(
          db,
          'studentAwardWins',
          awardWinDocId(nomination.periodKey, nomination.awardType, nomination.studentId)
        ));
      }
    }

    await batch.commit();
  };
}

/**
 * Take one card off the Shout-out Wall and leave the approval as it is, so
 * Kim can put it back up with the rest of the month when she is ready. The
 * same delete the reopen path makes; Director only in the rules.
 */
export async function takeDownAwardWin(winId: string): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const batch = writeBatch(db);
  batch.delete(doc(db, 'studentAwardWins', winId));
  await batch.commit();
}

export interface StrayAwardMergeResult {
  moved: number;
  duplicates: number;
  replaced: number;
  left: number;
  /**
   * How many people's picks could not be moved this time. Each person moves
   * in her own transaction, so the others already moved; a second tap
   * finishes the rest. 0 on a clean run.
   */
  failed: number;
}

const STRAY_MERGE_ERRORS: Record<string, string> = {
  director_only: 'Only the Director can bring picks together.',
  round_mismatch: 'The round changed while this page was open. Refresh and look at the preview again.',
  too_many_requests: 'That was a lot of taps. Wait a minute and try again.',
};

/**
 * Kim's one tap on "Bring stray picks into ..." (netlify/functions/
 * studentAwardMerge.mjs). The server recomputes the plan from fresh reads
 * with her choices; nothing here decides what moves.
 */
export async function requestStrayAwardMerge(
  targetPeriod: string,
  choices: StrayMergeChoices
): Promise<StrayAwardMergeResult> {
  const me = getAuth().currentUser;
  if (!me) throw new Error('Sign in required.');
  const response = await fetch('/.netlify/functions/studentAwardMerge', {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: await functionHeaders(me),
    body: JSON.stringify({
      targetPeriod,
      include: choices.include ?? [],
      exclude: choices.exclude ?? [],
      useInstead: choices.useInstead ?? {},
    }),
  });
  const payload = (await response.json().catch(() => ({}))) as Partial<Omit<StrayAwardMergeResult, 'failed'>> & {
    error?: string;
    ok?: boolean;
    partial?: boolean;
    failedGroups?: unknown;
  };
  const counts = () => ({
    moved: Number(payload.moved ?? 0),
    duplicates: Number(payload.duplicates ?? 0),
    replaced: Number(payload.replaced ?? 0),
    left: Number(payload.left ?? 0),
  });
  // SOME MOVED, ONE DID NOT (refute of #1266). The server moves each person
  // in her own transaction, so a failure partway leaves the earlier ones
  // moved; this is a result to show, never "nothing was moved".
  if (response.ok && payload.partial === true) {
    const failed = Array.isArray(payload.failedGroups) ? payload.failedGroups.length : 1;
    return { ...counts(), failed: Math.max(1, failed) };
  }
  // Only the server's own "ok" counts as done: a page that answered with
  // anything else (an HTML error page, an empty body) moved nothing.
  if (!response.ok || payload.error || payload.ok !== true) {
    throw new Error(STRAY_MERGE_ERRORS[payload.error ?? ''] ?? 'That did not go through, and nothing was moved. Try again in a moment.');
  }
  return { ...counts(), failed: 0 };
}

export async function fetchAwardNominationsOnce(): Promise<StudentAwardNominationDoc[]> {
  if (!db) throw new Error('Firestore not initialized.');
  const snap = await getDocs(query(collection(db, 'studentAwardNominations'), orderBy('createdAt', 'desc')));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<StudentAwardNominationDoc, 'id'>) }));
}
